// ============================================================
// File: client/src/scenes/transactions/TransactionForm.jsx
// Purpose: Upload transaction with flexible breakdowns (Loan/Contribution/Fine)
// ============================================================
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  Typography,
  IconButton,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { gql, useQuery } from "@apollo/client";
import { groupClient } from "state/apolloClient"; // for contribution schedules

// ---- GraphQL ----
// Use only NOT-PAID loan repayment schedules for the dropdown
const GET_UNPAID_LOAN_SCHEDULES = gql`
  query {
    loanRepaymentSchedulesNotPaid {
      applicantUserId
      dueDate
      groupId
      id
      interestDue
      interestPaid
      loanApplicationId
      principalDue
      principalPaid
      status
      statusId
    }
  }
`;

const GET_CONTRIBUTION_SCHEDULE = gql`
  query ContributionScheduleByUser {
    contributionScheduleByUser(userId: null, paid: false) {
      createdBy
      createdDate
      dueDate
      expectedAmount
      groupId
      id
      month
      monthId
      paid
      scheduleYear
      updatedBy
      updatedDate
      userId
    }
  }
`;

const GET_PENDING_MEMBER_FINES = gql`
  query PendingMemberFineView {
    pendingMemberFineView {
      id
      fine
      dueDate
      amountDue
      amountPaid
      fineTypeId
    }
  }
`;

const SCHEDULE_TYPES = [
  { id: 1, label: "Loan" },
  { id: 2, label: "Contribution" },
  { id: 3, label: "Fine" },
];

const currency = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? n.toLocaleString()
    : n
    ? Number(n).toLocaleString()
    : "-";

const TransactionForm = ({ open, onClose, onSubmit, loading }) => {
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [breakdowns, setBreakdowns] = useState([
    { scheduleTypeId: 1, scheduleType: "Loan", scheduleId: "", amount: "" },
  ]);

  // Load schedules
  // Loans come from the Account service (this scene's provider)
  const { data: loanData, loading: loadingLoans } = useQuery(
    GET_UNPAID_LOAN_SCHEDULES,
    { skip: !open }
  );

  // Contributions come from the Group service
  const { data: contribData, loading: loadingContrib } = useQuery(
    GET_CONTRIBUTION_SCHEDULE,
    {
      client: groupClient,
      skip: !open,
    }
  );

  // Pending fines come from the Group service
  const { data: finesData, loading: loadingFines } = useQuery(
    GET_PENDING_MEMBER_FINES,
    {
      client: groupClient,
      skip: !open,
    }
  );

  useEffect(() => {
    if (!open) {
      setConfirmationMessage("");
      setBreakdowns([
        { scheduleTypeId: 1, scheduleType: "Loan", scheduleId: "", amount: "" },
      ]);
    }
  }, [open]);

  const loanOptions = useMemo(
    () => {
      const loanRows = loanData?.loanRepaymentSchedulesNotPaid || [];
      return loanRows.map((r) => {
        const outstanding =
          (r.principalDue - r.principalPaid) +
          (r.interestDue - r.interestPaid);
        return {
          id: r.id,
          label: `#${r.id} • App ${r.loanApplicationId} • Due ${new Date(
            r.dueDate
          ).toLocaleDateString()} • Status ${r.status} • Outstanding KES ${currency(
            outstanding
          )}`,
        };
      });
    },
    [loanData?.loanRepaymentSchedulesNotPaid]
  );

  const contribOptions = useMemo(
    () => {
      const contribRows = contribData?.contributionScheduleByUser || [];
      return contribRows.map((r) => ({
        id: r.id,
        label: `#${r.id} • ${r.month}/${r.scheduleYear} • Due ${new Date(
          r.dueDate
        ).toLocaleDateString()} • Expected KES ${currency(r.expectedAmount)}`,
      }));
    },
    [contribData?.contributionScheduleByUser]
  );

  const fineOptions = useMemo(
    () => {
      const fineRows = finesData?.pendingMemberFineView || [];
      return fineRows.map((r) => {
        const outstanding = Number(r.amountDue || 0) - Number(r.amountPaid || 0);
        return {
          id: r.id,
          label: `#${r.id} • ${r.fine || `Type ${r.fineTypeId}`} • Due ${new Date(
            r.dueDate
          ).toLocaleDateString()} • Outstanding KES ${currency(outstanding)}`,
        };
      });
    },
    [finesData?.pendingMemberFineView]
  );

  const addRow = () =>
    setBreakdowns((prev) => [
      ...prev,
      { scheduleTypeId: 1, scheduleType: "Loan", scheduleId: "", amount: "" },
    ]);

  const removeRow = (idx) =>
    setBreakdowns((prev) => prev.filter((_, i) => i !== idx));

  const updateRow = (idx, key, value) => {
    setBreakdowns((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );
  };

  const handleTypeChange = (idx, typeId) => {
    const st = SCHEDULE_TYPES.find((t) => t.id === Number(typeId));
    setBreakdowns((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              scheduleTypeId: st.id,
              scheduleType: st.label,
              scheduleId: "",
            }
          : row
      )
    );
  };

  const canSubmit = useMemo(() => {
    const hasMsg =
      confirmationMessage && confirmationMessage.trim().length > 0;
    const validRows = breakdowns.every(
      (b) =>
        b.scheduleTypeId &&
        b.scheduleId !== "" &&
        b.amount !== "" &&
        Number(b.amount) > 0
    );
    return hasMsg && breakdowns.length > 0 && validRows;
  }, [confirmationMessage, breakdowns]);

  const breakdownTotal = useMemo(
    () =>
      breakdowns.reduce((sum, row) => {
        const amount = Number.parseFloat(row.amount);
        return sum + (Number.isNaN(amount) ? 0 : amount);
      }, 0),
    [breakdowns]
  );

  const submit = () => {
    if (!canSubmit) return;

    const payload = {
      confirmationMessage,
      transactionTypeId: 1,
      breakdowns: breakdowns.map((b) => ({
        scheduleType:
          SCHEDULE_TYPES.find((t) => t.id === Number(b.scheduleTypeId))
            ?.label || "",
        scheduleTypeId: Number(b.scheduleTypeId),
        scheduleId: Number(b.scheduleId),
        amount: Number.parseFloat(b.amount),
      })),
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Upload New Transaction</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Transaction Message"
            placeholder="Paste the full transaction confirmation message here..."
            margin="dense"
            multiline
            rows={5}
            value={confirmationMessage}
            onChange={(e) => setConfirmationMessage(e.target.value)}
          />

          <Box mt={2} component={Paper} variant="outlined" sx={{ p: 2 }}>
            <Box
              display="flex"
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
              mb={1}
            >
              <Typography variant="h6">Breakdowns</Typography>
              <Box
                display="flex"
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="flex-end"
                flexWrap="wrap"
                rowGap={1}
                columnGap={1.5}
                width={{ xs: "100%", sm: "auto" }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Running Total: <strong>KES {currency(breakdownTotal)}</strong>
                </Typography>
                <Tooltip title="Add another line item (Loan / Contribution / Fine)">
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Add />}
                      onClick={addRow}
                      disabled={loading}
                      sx={{
                        fontWeight: 700,
                        borderWidth: 1.5,
                        px: 1.75,
                        minHeight: 40,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          borderWidth: 1.5,
                        },
                      }}
                    >
                      Add Breakdown
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {breakdowns.map((row, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Schedule Type"
                      value={row.scheduleTypeId}
                      onChange={(e) => handleTypeChange(idx, e.target.value)}
                      disabled={loading}
                    >
                      {SCHEDULE_TYPES.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Selector based on type */}
                  {Number(row.scheduleTypeId) === 1 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Loan Repayment Schedule"
                        value={row.scheduleId}
                        onChange={(e) => updateRow(idx, "scheduleId", e.target.value)}
                        disabled={loading || loadingLoans}
                      >
                        {loanOptions.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {Number(row.scheduleTypeId) === 2 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Contribution Schedule"
                        value={row.scheduleId}
                        onChange={(e) => updateRow(idx, "scheduleId", e.target.value)}
                        disabled={loading || loadingContrib}
                      >
                        {contribOptions.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {Number(row.scheduleTypeId) === 3 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Fine Schedule"
                        value={row.scheduleId}
                        onChange={(e) => updateRow(idx, "scheduleId", e.target.value)}
                        disabled={loading || loadingFines}
                      >
                        {fineOptions.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Amount (KES)"
                      value={row.amount}
                      onChange={(e) => updateRow(idx, "amount", e.target.value)}
                      inputProps={{ min: 0, step: "0.01" }}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <Box display="flex" justifyContent="flex-end">
                      <IconButton
                        onClick={() => removeRow(idx)}
                        disabled={loading || breakdowns.length === 1}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={loading}
          sx={{ fontWeight: 600, minHeight: 40, px: 2.25 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={submit}
          disabled={loading || !canSubmit}
          sx={{ fontWeight: 700, minHeight: 40, px: 2.5 }}
        >
          {loading ? "Submitting..." : "Submit Transaction"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionForm;
